import { proxyHttpBase } from '../agent/config';

export type StockSource = 'pixabay' | 'openverse' | 'pexels';

/** One search hit, mirroring the proxy's `StockResult` shape. */
export interface StockResult {
  source: StockSource;
  sourceId: string;
  thumbUrl: string;
  fullUrl: string;
  width: number;
  height: number;
  license: string;
  attribution?: string;
  srcPageUrl: string;
}

export interface StockSearchResponse {
  source: StockSource | null;
  results: StockResult[];
}

/**
 * Map the raw `/stock/search` JSON into typed results, dropping any entry that
 * lacks the two fields the picker/import actually need (`fullUrl`, `thumbUrl`).
 */
export function mapStockResponse(raw: unknown): StockSearchResponse {
  const obj = (raw ?? {}) as { source?: unknown; results?: unknown };
  const source = obj.source === 'pixabay' || obj.source === 'openverse' || obj.source === 'pexels' ? obj.source : null;
  const list = Array.isArray(obj.results) ? obj.results : [];
  const results: StockResult[] = [];
  for (const r of list) {
    const o = r as Partial<StockResult>;
    if (typeof o.fullUrl === 'string' && typeof o.thumbUrl === 'string') {
      results.push({
        source: (o.source as StockSource) ?? source ?? 'pixabay',
        sourceId: String(o.sourceId ?? ''),
        thumbUrl: o.thumbUrl,
        fullUrl: o.fullUrl,
        width: Number(o.width ?? 0),
        height: Number(o.height ?? 0),
        license: String(o.license ?? ''),
        attribution: typeof o.attribution === 'string' ? o.attribution : undefined,
        srcPageUrl: String(o.srcPageUrl ?? ''),
      });
    }
  }
  return { source, results };
}

/** Fetch stock-photo results from the proxy's origin-gated HTTP search route. */
export async function searchStock(
  query: string,
  opts: { source?: StockSource; perPage?: number } = {},
): Promise<StockSearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (opts.source) params.set('source', opts.source);
  if (opts.perPage) params.set('perPage', String(opts.perPage));
  const res = await fetch(`${proxyHttpBase()}/stock/search?${params.toString()}`);
  if (!res.ok) throw new Error(`stock search failed: HTTP ${res.status}`);
  return mapStockResponse(await res.json());
}
